import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { palette } from '@m12s/component-library';
import { farSyncAlt } from 'lib/icons';
import { useSelector } from 'react-redux';
import { getSelectWorkOrderOpOptions } from 'lib/selectors/getSelectWorkOrderOp';
import { useJobDisplayName } from 'lib/hooks/useJobDisplayName';
import { SpinningIcon } from '../styled';

const DefaultJobCardTitle = styled.h5`
  color: ${({ disabled }) => {
    return disabled ? palette.Grey500 : palette.Grey700;
  }};
  margin: 0;
`;

const StandardJobCardTitle = styled(DefaultJobCardTitle)`
  font-weight: 600;
  font-size: 1.5rem;
`;

const SearchJobCardTitle = styled(DefaultJobCardTitle)`
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1rem;
`;

const JobCardSubTitle = styled.h6`
  margin: 0;
  color: ${({ disabled }) => {
    return disabled ? palette.Grey500 : palette.Grey600;
  }};
  font-weight: 500;
  font-size: 1.25rem;
`;

const RootContainer = styled.div`
  padding: ${({ isSearchMode }) => {
    return isSearchMode ? '0.5rem 0.75rem' : '1rem';
  }};
  border-bottom: 1px solid ${palette.Grey300};
`;

const JobCardTitle = ({ workOrderOp, active, dimmed = active }) => {
  const { isSearchMode } = useSelector(getSelectWorkOrderOpOptions);
  const { getOrderOpDisplayName } = useJobDisplayName();

  // Memoize the order op display name to avoid recomputing it on every render
  const orderOpDisplayName = useMemo(() => {
    return getOrderOpDisplayName(workOrderOp);
  }, [getOrderOpDisplayName, workOrderOp]);

  const part1 = `${workOrderOp?.workOrderId}`;

  const searchHeaderText =
    workOrderOp && workOrderOp?.workOrder
      ? isSearchMode
        ? `${part1} | ${orderOpDisplayName}`
        : part1
      : 'Unknown';

  const searchSubheaderText =
    workOrderOp && workOrderOp?.workOrder ? orderOpDisplayName : 'Unknown';

  const TitleComponent = isSearchMode
    ? SearchJobCardTitle
    : StandardJobCardTitle;

  return (
    <RootContainer isSearchMode={isSearchMode}>
      <TitleComponent disabled={dimmed} isSearchMode={isSearchMode}>
        {active && (
          <SpinningIcon icon={farSyncAlt} style={{ marginRight: '.5rem' }} />
        )}
        {searchHeaderText}
      </TitleComponent>
      {!isSearchMode && (
        <JobCardSubTitle disabled={dimmed}>
          {searchSubheaderText}
        </JobCardSubTitle>
      )}
    </RootContainer>
  );
};

JobCardTitle.propTypes = {
  workOrderOp: PropTypes.object,
  active: PropTypes.bool,
  // Greys the title when true. Defaults to `active`, but the change-job/split
  // flow passes false for the current run so a selectable card doesn't read as
  // disabled while still showing the "current run" spinner.
  dimmed: PropTypes.bool,
};

export default JobCardTitle;
